import { TypedUseSelectorHook, useSelector } from 'react-redux';

import {
  AnyAction,
  combineReducers,
  configureStore,
  EnhancedStore,
  ListenerMiddleware,
  MiddlewareArray,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { CurriedGetDefaultMiddleware } from '@reduxjs/toolkit/dist/getDefaultMiddleware';
import { ThunkMiddleware } from 'redux-thunk';

import reviewsSliceReducers from '../features/app-store/components/RatingsAndReviews/store/slices/slices/reviews.slice';
import { statisticsFiltersListener } from '../features/app-store/components/Statistics/listeners/statistics-filters.listener';
import statisticsSliceReducers from '../features/app-store/components/Statistics/store/slices/statistics.slice';
import { appsReleasesListener } from '../features/app-store/listeners/app-releases.listener';
import { appReviewsFiltersListener } from '../features/app-store/listeners/app-reviews-filters.listener';
import { appsFilterListener } from '../features/app-store/listeners/apps-filter.listener';
import appsFilesSliceReducers from '../features/app-store/store/slices/apps-files.slice';
import appsSliceReducers from '../features/app-store/store/slices/apps.slice';
import releasesSliceReducers from '../features/app-store/store/slices/releases.slice';
import { campaignReportsListener } from '../features/campaigns-management/listeners/campaign-reports.listener';
import { campaignsFilterListener } from '../features/campaigns-management/listeners/campaigns-filter.listener';
import campaignReportsSliceReducers from '../features/campaigns-management/store/slices/campaign-reports.slice';
import campaignBasicInfoSliceReducers from '../features/campaigns-management/store/slices/campaigns-basic-info.slice';
import campaignVehiclePopulationSliceReducers from '../features/campaigns-management/store/slices/campaigns-vehicle-population.slice';
import campaignsSliceReducers from '../features/campaigns-management/store/slices/campaigns.slice';
import { firmwareFilterListener } from '../features/firmware-management/listeners/firmware-filter.listener';
import { firmwarePaginationListener } from '../features/firmware-management/listeners/firmware-pagination.listener';
import firmwareSliceReducers from '../features/firmware-management/store/slices/firmware.slice';
import { mandantSliceReducers } from '../features/mandant/slices/mandant.slice';
import { webGamesFiltersListener } from '../features/web-games/listener/web-games-filters.listener';
import webGamesSliceReducers from '../features/web-games/store/slices/web-game.slice';
import { AppRootState } from './models/app-state.model';
import rootSliceReducers from './slices/root.slice';
import {
  APPS,
  APPS_FEATURE,
  APPS_FILES,
  BASIC_INFO,
  CAMPAIGNS,
  CAMPAIGNS_FEATURE,
  FIRMWARE,
  MANDANTS,
  RELEASES,
  REVIEWS,
  ROOT,
  STATISTICS,
  VEHICLE_POPULATION,
  VEHICLE_REPORTS,
  WEB_GAMES,
} from './utils/files-names';

export const store: EnhancedStore<
  AppRootState,
  AnyAction,
  MiddlewareArray<
    [
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,
      ListenerMiddleware<
        AppRootState,
        ThunkDispatch<AppRootState, undefined, AnyAction>,
        unknown
      >,

      ThunkMiddleware<AppRootState, AnyAction, undefined>
    ]
  >
> = configureStore({
  reducer: {
    [APPS_FEATURE]: combineReducers({
      [APPS]: appsSliceReducers,
      [RELEASES]: releasesSliceReducers,
      [APPS_FILES]: appsFilesSliceReducers,
      [STATISTICS]: statisticsSliceReducers,
    }),
    [ROOT]: rootSliceReducers,
    [MANDANTS]: mandantSliceReducers,
    [FIRMWARE]: firmwareSliceReducers,
    [CAMPAIGNS_FEATURE]: combineReducers({
      [BASIC_INFO]: campaignBasicInfoSliceReducers,
      [VEHICLE_POPULATION]: campaignVehiclePopulationSliceReducers,
      [VEHICLE_REPORTS]: campaignReportsSliceReducers,
      [CAMPAIGNS]: campaignsSliceReducers,
    }),
    [REVIEWS]: reviewsSliceReducers,
    [WEB_GAMES]: webGamesSliceReducers,
  },
  middleware: (
    getDefaultMiddleware: CurriedGetDefaultMiddleware<AppRootState>
  ) => {
    return getDefaultMiddleware({
      serializableCheck: false,
    }).prepend(
      appsFilterListener.middleware,
      appsReleasesListener.middleware,
      firmwareFilterListener.middleware,
      firmwarePaginationListener.middleware,
      campaignsFilterListener.middleware,
      campaignReportsListener.middleware,
      appReviewsFiltersListener.middleware,
      statisticsFiltersListener.middleware,
      webGamesFiltersListener.middleware
    );
  },
});

export const useAppSelector: TypedUseSelectorHook<AppRootState> = useSelector;
